import * as React from 'react'
import grapesjs from 'grapesjs'
import GjsEditor, {
  AssetsProvider,
  Canvas,
  ModalProvider,
  WithEditor,
} from '@grapesjs/react'
import 'grapesjs/dist/css/grapes.min.css'
import './editor-theme.css'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import Topbar from './components/Topbar'
import CanvasToolbar from './components/CanvasToolbar'
import RightSidebar from './components/RightSidebar'
// Lazy load heavy components
const AIWebsiteGenerator = React.lazy(() => import('./components/AIWebsiteGenerator'))
const DomainManager = React.lazy(() => import('./components/DomainManager'))
const AnalyticsDashboard = React.lazy(() => import('./components/analytics/AnalyticsDashboard'))
const TrackingSetup = React.lazy(() => import('./components/analytics/TrackingSetup'))
const GoogleAnalyticsSetup = React.lazy(() => import('./components/analytics/GoogleAnalyticsSetup'))
const FacebookPixelSetup = React.lazy(() => import('./components/analytics/FacebookPixelSetup'))
const MapboxSettings = React.lazy(() => import('./components/MapboxSettings'))
const SettingsModal = React.lazy(() => import('./components/SettingsModal'))
import { Dialog, DialogContent, DialogTitle, DialogActions, Button, IconButton, CircularProgress, Box } from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import { DomainProvider } from './contexts/DomainContext'
import { useAuth } from './contexts/AuthContext'
import dashboardService from './services/dashboardService'
import apiService from './services/api'
import { dealPageId } from './utils/dealPageId'
import { buildDealListingPage } from './data/dealListingTemplate'
import { extractSiteChrome } from './utils/siteChrome'
import { AllBlocks } from './components/Block.const'
import { getAllPlugins, initializeReactPlugins, getPluginStatus } from './plugins/pluginManager'
import { 
  initializePropertyPageListener, 
  initializePropertySSE, 
  closePropertySSE,
  startPropertyPagePolling 
} from './services/propertyPageService'
import { compressData, decompressData, shouldCompress, formatBytes } from './utils/compression'
import { addToQueue, processQueue, setupAutoProcess, getQueueStats } from './utils/saveQueue'
import changeTracker, { setupChangeTracking, filterDirtyPages, setupStorageEvents, setupPageEvents, createStorageError } from './utils/changeTracking'
import MainNav from './components/navigation/MainNav'
import DashboardOverview from './components/DashboardOverview'
import NavigationSettingsModal from './components/NavigationSettingsModal'

// API Base URL configuration
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:4000'

// Generate a MongoDB-compatible ObjectId for default pages
// This ensures new pages can be saved to the database
const generateObjectId = () => {
  const timestamp = Math.floor(Date.now() / 1000).toString(16).padStart(8, '0');
  const randomPart = Array.from({ length: 16 }, () =>
    Math.floor(Math.random() * 16).toString(16)
  ).join('');
  return timestamp + randomPart;
};

// Debug logging utility - enable via localStorage.setItem('gjs_debug', 'true') or URL ?debug=true
// IMPORTANT: Uses Function constructor to bypass production console stripping
const DEBUG_ENABLED = () => {
  if (typeof window === 'undefined') return false;
  try {
    const urlDebug = new URLSearchParams(window.location.search).get('debug') === 'true';
    const localDebug = localStorage.getItem('gjs_debug') === 'true';
    return urlDebug || localDebug;
  } catch (e) {
    return false;
  }
};

// Force logging even in production by using Function constructor
const forceLog = (...args) => {
  try {
    // This bypasses production console stripping
    const logFn = Function.prototype.bind.call(console.log, console);
    logFn('[GJS]', ...args);
  } catch (e) {
    // Fallback
    console.log('[GJS]', ...args);
  }
};

const forceWarn = (...args) => {
  try {
    const warnFn = Function.prototype.bind.call(console.warn, console);
    warnFn('[GJS]', ...args);
  } catch (e) {
    console.warn('[GJS]', ...args);
  }
};

const forceError = (...args) => {
  try {
    const errorFn = Function.prototype.bind.call(console.error, console);
    errorFn('[GJS]', ...args);
  } catch (e) {
    console.error('[GJS]', ...args);
  }
};

const debug = {
  log: (...args) => DEBUG_ENABLED() && forceLog(...args),
  warn: (...args) => DEBUG_ENABLED() && forceWarn(...args),
  error: (...args) => forceError(...args), // Always show errors
  group: (label) => {
    if (DEBUG_ENABLED()) {
      try {
        const groupFn = Function.prototype.bind.call(console.group, console);
        groupFn(`[GJS] ${label}`);
      } catch (e) {
        forceLog(`--- ${label} ---`);
      }
    }
  },
  groupEnd: () => {
    if (DEBUG_ENABLED()) {
      try {
        const groupEndFn = Function.prototype.bind.call(console.groupEnd, console);
        groupEndFn();
      } catch (e) {
        // noop
      }
    }
  },
  table: (data) => {
    if (DEBUG_ENABLED()) {
      try {
        const tableFn = Function.prototype.bind.call(console.table, console);
        tableFn(data);
      } catch (e) {
        forceLog('Table:', JSON.stringify(data, null, 2));
      }
    }
  },
  // Always log - for critical debugging
  force: (...args) => forceLog(...args),
};

// Expose debug controls to window for easy access
if (typeof window !== 'undefined') {
  window.gjsDebug = {
    enable: () => {
      localStorage.setItem('gjs_debug', 'true');
      forceLog('GrapesJS debug ENABLED. Refresh page to see all logs.');
    },
    disable: () => {
      localStorage.removeItem('gjs_debug');
      forceLog('GrapesJS debug DISABLED. Refresh page.');
    },
    status: () => forceLog('Debug:', DEBUG_ENABLED() ? 'ENABLED' : 'DISABLED'),
    metrics: () => {
      forceLog('Save Metrics:', JSON.stringify(window.saveMetrics || {}, null, 2));
    },
    compression: () => {
      forceLog('Compression Metrics:', JSON.stringify(window.compressionMetrics || {}, null, 2));
    },
    // Test logging works
    test: () => {
      forceLog('TEST: If you see this, logging works!');
      forceWarn('TEST: Warning log');
      forceError('TEST: Error log');
    }
  };
  // Always show this initial message
  forceLog('GrapesJS Debug available: window.gjsDebug.enable() | window.gjsDebug.test()');
}

// Loading component for Suspense
const SuspenseLoader = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
    <CircularProgress />
  </Box>
)

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <Dialog open={true} onClose={() => this.setState({ hasError: false })}>
          <DialogTitle>Error Loading Component</DialogTitle>
          <DialogContent>
            <p>Sorry, there was an error loading the Domain Manager. Please try refreshing the page.</p>
            <pre style={{ background: '#f5f5f5', padding: '10px', overflow: 'auto', fontSize: '12px' }}>
              {this.state.error?.toString()}
            </pre>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ hasError: false })}>Close</Button>
          </DialogActions>
        </Dialog>
      )
    }

    return this.props.children
  }
}

// Maps the app's dx-* dark design tokens (src/editor-theme.css `.dreamx-shell`)
// onto MUI's theme. Values are hardcoded (not `var(--dx-*)`) because MUI's
// Dialog/Modal render through a React portal attached to document.body,
// outside the `.dreamx-shell` element that defines those CSS custom
// properties — a var() reference would silently fail to resolve there.
export const theme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#0d0d0f', // --dx-bg
      paper: '#17171a',   // --dx-surface
    },
    text: {
      primary: '#ededf2',   // --dx-text
      secondary: '#a3a3ad', // --dx-text-2
      disabled: '#6b6b76',  // --dx-text-3
    },
    divider: '#1f1f25', // --dx-border-soft
    primary: {
      main: '#3b82f6', // --dx-accent
    },
    success: {
      main: '#10b981', // --dx-green
    },
    warning: {
      main: '#f59e0b', // --dx-amber
    },
    error: {
      main: '#f43f5e', // --dx-rose
    },
  },
  typography: {
    fontFamily: [
      'Inter', 'SF Pro Text', 'SF Pro Display', '-apple-system', 'BlinkMacSystemFont',
      '"Segoe UI"', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', '"Fira Sans"',
      '"Droid Sans"', '"Helvetica Neue"', 'sans-serif',
    ].join(','),
  },
})

// Tracks whether the store endpoint is reachable. Set to false on 404 to
// prevent GrapesJS autosave from spamming a missing endpoint.
let storeEndpointAvailable = true;

// Custom storage plugin that handles all storage operations
// This prevents GrapesJS from making its own internal fetch requests
const customStoragePlugin = (editor) => {
  // Register a custom storage type that only uses our handlers
  editor.Storage.add('godreamx', {
    async load() {
      debug.group('customStorage:load');
      debug.force('customStorage load START');

      try {
        const token = localStorage.getItem('jwt') || localStorage.getItem('token');
        debug.log('Token present:', !!token);

        if (!token) {
          debug.warn('No auth token, returning empty object so GrapesJS keeps its config default page');
          debug.groupEnd();
          return {};
        }

        const response = await fetch(`${API_BASE}/api/pages/grapesjs/load`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`Load failed: ${response.status}`);
        }

        const result = await response.json();
        debug.log('Load result:', result);
        debug.groupEnd();

        // CRITICAL: GrapesJS's loadData() only skips clearing/loading when the
        // resolved object is truly empty (isEmptyObj check on key count, not on
        // pages.length). Returning {pages: [], assets: [], styles: []} for a
        // brand-new/just-wiped org still has 3 keys, so GrapesJS treats it as
        // "real" data: it clears every storable (including the config-time
        // default `pageManager.pages` Home Page) and then loads zero pages,
        // leaving the editor with NO pages at all — an invalid state that
        // crashes ("t.on is not a function") the next time anything tries to
        // render/select the (nonexistent) main page. If there's nothing saved
        // yet, return {} instead so GrapesJS's default page survives.
        if (!result.pages || result.pages.length === 0) {
          debug.force('customStorage load: no saved pages, returning {} to preserve default page');
          return {};
        }
        return result;
      } catch (error) {
        debug.error('Load error:', error);
        debug.groupEnd();
        return {};
      }
    },

    async store(data) {
      if (!storeEndpointAvailable) {
        debug.warn('Save endpoint unavailable (404 received previously) — skipping autosave');
        return { success: false, skipped: true };
      }

      debug.group('customStorage:store');
      debug.force('customStorage store START - saving data');
      debug.log('Store called with data:', { pagesCount: data?.pages?.length, hasData: !!data });

      // Set flag to prevent overlapping saves (used by page migration)
      window._gjsSaveInProgress = true;

      try {
        let token = localStorage.getItem('jwt') || localStorage.getItem('token');
        let pagesData = data.pages || [];

        debug.log('Token present:', !!token);
        debug.log('Pages to save:', pagesData.length);
        debug.force('💾 customStorage store: Token=' + (token ? 'YES' : 'NO') + ', Pages=' + pagesData.length);

        // Early return if no data to save
        if (!data || !pagesData || pagesData.length === 0) {
          debug.warn('No pages data to save, skipping');
          debug.groupEnd();
          debug.force('customStorage store END - no data to save');
          window._gjsSaveInProgress = false;
          return { success: true, message: 'No pages to save' };
        }

        // Performance monitoring
        const saveStartTime = performance.now();

        // CRITICAL: Ensure all page IDs are valid MongoDB ObjectIds (24-char hex)
        // GrapesJS can create pages with random IDs that the backend won't accept
        pagesData = pagesData.map(page => {
          const id = page.id;
          // Check if ID is a valid 24-character hex string (MongoDB ObjectId format)
          const isValidObjectId = id && id.length === 24 && /^[0-9a-f]{24}$/i.test(id);
          if (!isValidObjectId) {
            // Generate a new MongoDB-compatible ObjectId
            const newId = generateObjectId();
            console.log(`🔄 Migrating page ID from "${id}" to "${newId}" during save`);
            // Also update the live GrapesJS page model, not just this outgoing
            // copy — otherwise the next autosave still sees the old invalid id,
            // mints yet another new id, and the backend (which upserts by id)
            // creates a brand new duplicate page every cycle instead of ever
            // converging on the one we just persisted.
            editor.Pages.get(id)?.set('id', newId);
            return { ...page, id: newId };
          }
          return page;
        });

        // Keep a reference to all migrated pages so the fallback below uses
        // the already-migrated IDs rather than the pre-migration data.pages.
        const migratedPages = pagesData;

        // PRIORITY 3: Incremental saves - only save dirty pages
        const dirtyPageIds = changeTracker.getDirtyPages();
        console.log('🔍 Debug - Dirty page IDs:', dirtyPageIds);
        console.log('🔍 Debug - Page IDs in payload:', pagesData.map(p => p.id));

        if (dirtyPageIds.length > 0 && dirtyPageIds.length < pagesData.length) {
          const originalCount = pagesData.length;
          pagesData = pagesData.filter(page => {
            const isDirty = dirtyPageIds.includes(page.id);
            console.log(`🔍 Page ${page.id}: ${isDirty ? 'DIRTY' : 'clean'}`);
            return isDirty;
          });
          console.log(`📝 Incremental save: ${pagesData.length}/${originalCount} pages (${dirtyPageIds.length} dirty)`);

          // If dirty IDs don't match any migrated page IDs (e.g. changeTracker
          // holds pre-migration GrapesJS IDs), save all migrated pages so we
          // never send an empty or pre-migration payload.
          if (pagesData.length === 0) {
            console.log('⚠️ No dirty pages matched, saving all migrated pages');
            pagesData = migratedPages;
          }
        }

        const pageCount = pagesData.length;

        console.log('💾 GrapesJS Auto-saving...', pageCount, 'pages');
        console.log('🔑 Token:', token ? 'Present' : 'Missing');

        // Update save status to 'saving'
        if (window.updateSaveStatus) {
          window.updateSaveStatus('saving');
        }

        // Track save metrics
        if (!window.saveMetrics) {
          window.saveMetrics = {
            totalSaves: 0,
            successfulSaves: 0,
            failedSaves: 0,
            totalRetries: 0,
            averageSaveTime: 0,
            saveTimes: []
          };
        }
        window.saveMetrics.totalSaves++;

        // PRIORITY 3: Compression for large payloads
        let payload = { pages: pagesData };
        let compressionInfo = null;

        if (shouldCompress(payload)) {
          const compressed = compressData(payload);
          if (compressed.compressed) {
            compressionInfo = compressed;
            payload = {
              compressed: true,
              data: compressed.data
            };
            console.log(`🗜️ Compressed: ${formatBytes(compressed.originalSize)} → ${formatBytes(compressed.compressedSize)} (${compressed.compressionRatio}x, ${compressed.compressionTime}ms)`);

            // Track compression metrics
            if (!window.compressionMetrics) {
              window.compressionMetrics = {
                totalCompressed: 0,
                totalSaved: 0,
                averageRatio: 0
              };
            }
            window.compressionMetrics.totalCompressed++;
            window.compressionMetrics.totalSaved += (compressed.originalSize - compressed.compressedSize);
            window.compressionMetrics.averageRatio =
              ((window.compressionMetrics.averageRatio * (window.compressionMetrics.totalCompressed - 1)) +
              parseFloat(compressed.compressionRatio)) / window.compressionMetrics.totalCompressed;
          }
        }

        // Retry logic with exponential backoff
        const saveWithRetry = async (retryCount = 0, maxRetries = 3) => {
          try {
            // Get fresh token for each retry attempt
            const currentToken = localStorage.getItem('jwt') || localStorage.getItem('token');

            const response = await fetch(`${API_BASE}/api/pages/grapesjs/store`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${currentToken}`
              },
              body: JSON.stringify(payload)
            });

            if (!response.ok) {
              // Check if it's an auth error
              if (response.status === 401) {
                console.log('🔑 Auth error during save, attempting token refresh...');
                // Try to refresh token
                if (window.authContext?.fetchUserProfile) {
                  await window.authContext.fetchUserProfile();
                  // Retry with new token
                  if (retryCount < maxRetries) {
                    console.log(`🔄 Retrying save after token refresh (attempt ${retryCount + 1}/${maxRetries})`);
                    window.saveMetrics.totalRetries++;
                    return saveWithRetry(retryCount + 1, maxRetries);
                  }
                }
              }

              // For other errors, retry with exponential backoff
              if (retryCount < maxRetries && response.status >= 500) {
                const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
                console.log(`🔄 Server error, retrying in ${delay}ms (attempt ${retryCount + 1}/${maxRetries})`);
                window.saveMetrics.totalRetries++;
                await new Promise(resolve => setTimeout(resolve, delay));
                return saveWithRetry(retryCount + 1, maxRetries);
              }

              // 404 means the endpoint doesn't exist — disable autosave to stop the loop
              if (response.status === 404) {
                storeEndpointAvailable = false;
                console.error('⛔ Save endpoint returned 404. Check that VITE_API_URL points to the correct backend. Autosave disabled until page reload.');
              }

              throw new Error(`Save failed: ${response.status} ${response.statusText}`);
            }

            return response.json();
          } catch (error) {
            // Network errors - retry with backoff
            if (retryCount < maxRetries && (error.name === 'TypeError' || error.message.includes('fetch'))) {
              const delay = Math.pow(2, retryCount) * 1000;
              console.log(`🔄 Network error, retrying in ${delay}ms (attempt ${retryCount + 1}/${maxRetries})`);
              window.saveMetrics.totalRetries++;
              await new Promise(resolve => setTimeout(resolve, delay));
              return saveWithRetry(retryCount + 1, maxRetries);
            }
            throw error;
          }
        };

        const result = await saveWithRetry();

        // Calculate save time
        const saveEndTime = performance.now();
        const saveTime = saveEndTime - saveStartTime;

        // Update metrics
        window.saveMetrics.successfulSaves++;
        window.saveMetrics.saveTimes.push(saveTime);
        if (window.saveMetrics.saveTimes.length > 100) {
          window.saveMetrics.saveTimes.shift(); // Keep only last 100
        }
        window.saveMetrics.averageSaveTime =
          window.saveMetrics.saveTimes.reduce((a, b) => a + b, 0) / window.saveMetrics.saveTimes.length;

        console.log(`✅ Saved ${pageCount} pages in ${saveTime.toFixed(0)}ms`);

        // Update save status to 'saved'
        if (window.updateSaveStatus) {
          window.updateSaveStatus('saved');
        }

        // Clear dirty pages after successful save
        changeTracker.clearAll();

        debug.log('Save resolved successfully:', result);
        debug.groupEnd();
        debug.force('customStorage store END - success');
        window._gjsSaveInProgress = false;
        return result;
      } catch (error) {
        debug.error('Save error caught:', error.message, error.stack);
        debug.force('❌ customStorage store CATCH - error:', error.message);

        // Update metrics
        if (window.saveMetrics) {
          window.saveMetrics.failedSaves++;
        }

        // Check if offline and queue the save
        if (!navigator.onLine) {
          console.log('📴 Offline detected, queuing save...');
          try {
            await addToQueue({ pages: data.pages || [] });
            console.log('📥 Added to offline queue');
            if (window.showNotification) {
              window.showNotification('Offline: Changes queued for later sync', 'warning');
            }
            if (window.updateSaveStatus) {
              window.updateSaveStatus('saved');
            }
            window._gjsSaveInProgress = false;
            return { success: true, queued: true };
          } catch (queueError) {
            console.error('❌ Failed to add to queue:', queueError);
          }
        }

        // Update save status to 'error'
        if (window.updateSaveStatus) {
          window.updateSaveStatus('error');
        }

        // Show user-friendly error
        if (error.message.includes('401')) {
          if (window.showNotification) {
            window.showNotification('Session expired. Please refresh the page.', 'error', 5000);
          }
        } else {
          if (window.showNotification) {
            window.showNotification('Failed to save changes. Please try again.', 'error', 5000);
          }
        }

        debug.log('Returning error result');
        debug.groupEnd();
        debug.force('customStorage store END - error');
        window._gjsSaveInProgress = false;
        // Return success: false but don't throw - this prevents "PromiseReject called on non-object"
        return { success: false, error: error.message };
      }
    }
  });
};

const gjsOptions = {
  height: '100vh',
  logLevel: import.meta.env.PROD ? 0 : 3,
  storageManager: {
    // Use our custom storage type 'godreamx' instead of 'remote'
    // This completely bypasses GrapesJS's internal RemoteStorage and its fetch calls
    // The custom storage is registered by the customStoragePlugin
    type: 'godreamx',
    autosave: true,
    // Must be true — this is what actually triggers our custom load()
    // handler (registered in customStoragePlugin) on init. It was false
    // with a comment claiming a manual load() call would happen elsewhere,
    // but no such call exists anywhere in this file, and a second,
    // contradictory comment near the bottom of onEditor() claims the
    // opposite — that "storage manager automatically loads data" and no
    // manual call is needed. Neither was true: with autoload false and no
    // manual call, load() was never invoked at all, so every fresh editor
    // mount silently discarded previously saved pages and started over
    // from the hardcoded default below, then autosaved that default as a
    // brand new set of duplicate Page documents. Our custom load() handles
    // its own auth token from localStorage (see customStoragePlugin) —
    // it doesn't rely on GrapesJS's internal fetch, so autoload is safe.
    autoload: true,
    stepsBeforeSave: 5,  // Increased from 3 to reduce save frequency
  },
  undoManager: { trackSelection: false },
  selectorManager: { componentFirst: true },
  // IMPORTANT: no custom `pageManager.pages` override here. This used to
  // supply a hand-built default page (with a pre-generated ObjectId "to
  // ensure it can be saved to MongoDB"), but that shorthand goes through a
  // different, less-exercised construction path in GrapesJS's Frame model
  // than real component/style data does, and two separate bugs traced back
  // to it this session: a raw `styles` string never getting parsed into the
  // CssComposer (crashing canvas mount with "t.on is not a function"), and,
  // after working around that by inlining CSS as a <style> tag inside
  // `component`, a runaway component:add loop on first mount. Rather than
  // keep patching this shorthand, don't use it — GrapesJS's own built-in
  // default page (its most heavily-exercised code path) is what every other
  // installation relies on. We don't need a pre-generated ObjectId either:
  // customStoragePlugin's store() already detects any non-ObjectId page id
  // and migrates it to a fresh one on first save (see the `isValidObjectId`
  // check below), so GrapesJS's own default id self-heals on its own.
  // Canvas configuration
  canvas: {
    scripts: [
      // Must load before Tailwind CDN — disables preflight so it doesn't fight UIKit's base styles
      "/tailwind-canvas-config.js",
      "https://cdn.tailwindcss.com",
      "https://cdn.jsdelivr.net/npm/uikit@3/dist/js/uikit.min.js",
    ],
    styles: [
      "https://cdn.jsdelivr.net/npm/uikit@3/dist/css/uikit.min.css",
    ],
    // Enable grid system
    grid: {
      enabled: true,
      size: 20, // Grid size in pixels
      color: 'rgba(0, 123, 255, 0.15)', // Slightly more visible blue grid lines
      snapToGrid: true, // Enable snap to grid
    },
    // Enable guidelines
    guidelines: {
      enabled: true,
      color: 'rgba(0, 123, 255, 0.5)', // Blue guidelines
      snapToGuidelines: true,
    },
  },
  blockManager: {
    custom: true,
    blocks: [
      ...AllBlocks.map((block) => {
        return {
          ...block,
          category: block.category || 'Blocks',
        }
      }),
    ],
  },
  // IMPORTANT: do not set `projectData` here. Per GrapesJS's own docs it's
  // "Initial project data... to load" — supplying it (even just for
  // default assets) makes GrapesJS use that data directly instead of ever
  // calling the storage manager's load(), regardless of `autoload`. This
  // was the root cause of every saved page silently failing to load all
  // session: confirmed by decompiling grapes.min.js's storage init logic
  // (`n ? loadData(n) : (canAutoload() ? load() : ...)`, where `n` is the
  // projectData value) and by our custom load()'s debug.force() start log
  // never once firing despite being unconditional. Default assets go
  // through assetManager.assets instead, which doesn't have this problem.
  assetManager: {
    assets: [
      'https://via.placeholder.com/350x250/78c5d6/fff',
      'https://via.placeholder.com/350x250/459ba8/fff',
      'https://via.placeholder.com/350x250/79c267/fff',
      'https://via.placeholder.com/350x250/c5d647/fff',
      'https://via.placeholder.com/350x250/f28c33/fff',
    ],
  },
}

export default function TemplateBuilder() {
  console.log('🎨 GoDreamX Web Builder component rendering...');
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editorReady, setEditorReady] = React.useState(false);
  React.useEffect(() => {
    if (!editorOpen) setEditorReady(false);
  }, [editorOpen]);
  const { user, currentOrg, currentSubdomain, crmDealRedirect } = useAuth();
  const [dashboardData, setDashboardData] = React.useState({
    websiteInfo: null,
    mlsConnections: [],
    analytics: null,
    packageInfo: null,
    loading: true
  });
  const [analyticsTimeRange, setAnalyticsTimeRange] = React.useState('7days');
  const [aiGeneratorOpen, setAIGeneratorOpen] = React.useState(false);
  const [navSettingsOpen, setNavSettingsOpen] = React.useState(false);
  // const [pageManagerOpen, setPageManagerOpen] = React.useState(false); // Removed - using native GrapesJS page manager
  const [domainManagerOpen, setDomainManagerOpen] = React.useState(false);
  const [analyticsDashboardOpen, setAnalyticsDashboardOpen] = React.useState(false);
  const [trackingSetupOpen, setTrackingSetupOpen] = React.useState(false);
  const [googleAnalyticsSetupOpen, setGoogleAnalyticsSetupOpen] = React.useState(false);
  const [facebookPixelSetupOpen, setFacebookPixelSetupOpen] = React.useState(false);
  const [mapboxSettingsOpen, setMapboxSettingsOpen] = React.useState(false);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  
  // Save status tracking
  const [saveStatus, setSaveStatus] = React.useState('saved'); // 'saving' | 'saved' | 'error'
  const [lastSaved, setLastSaved] = React.useState(null);
  const saveStatusRef = React.useRef({ status: 'saved', lastSaved: null });
  
  // Pages now managed by native GrapesJS Pages API
  // const { pages, createPage: contextCreatePage } = usePages(); // Removed - using native GrapesJS
  
  // Clean up invalid page IDs on mount
  React.useEffect(() => {
    // Clear the specific invalid page ID from change tracker
    if (window.changeTracker) {
      window.changeTracker.cleanupInvalidIds();
    }
    
    // Also clear from localStorage if present
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.includes('2sdIkkIWFRA6eEpG')) {
          localStorage.removeItem(key);
          console.log('🧹 Cleared invalid page ID from localStorage:', key);
        }
      });
    } catch (e) {
      console.warn('Could not clean localStorage:', e);
    }
  }, []);
  
  // Expose pages to window for reference - now using native GrapesJS Pages API
  React.useEffect(() => {
    if (window.editor) {
      window.pagesFromContext = window.editor.Pages.getAll();
    }
  }, []);

  // Fetch dashboard data when user is available
  React.useEffect(() => {
    if (user && !editorOpen) {
      fetchDashboardData();
    }
  }, [user, editorOpen, analyticsTimeRange]);

  // Deal Board → Template Builder: the app lands on the Overview tab by
  // default (editorOpen starts false) — the Editor, and with it the
  // onEditor() prefill logic below, only mounts once the agent manually
  // clicks in. A "Create Website" SSO hop should drop them straight into
  // the editor instead of requiring that extra click.
  // Gated on crmDealRedirect -- derived fresh from THIS redirect's own
  // ?source=deal, set by the backend only when its own deal_id query param
  // was present (routes/auth.js) -- not just prefillPending, so a plain
  // login/reload never auto-jumps into the editor just because the CRM
  // deal flag is still (or again) true server-side from a past session.
  React.useEffect(() => {
    if (crmDealRedirect && user?.activeCrmDeal?.prefillPending && !editorOpen) {
      setEditorOpen(true);
    }
  }, [user, editorOpen, crmDealRedirect]);

  const fetchDashboardData = async () => {
    try {
      setDashboardData(prev => ({ ...prev, loading: true }));
      
      const [websiteInfo, mlsConnections, analytics, packageInfo, topPages] = await Promise.all([
        dashboardService.getWebsiteInfo(user),
        dashboardService.getMLSConnections(user?.orgId),
        dashboardService.getAnalytics(analyticsTimeRange),
        dashboardService.getPackageInfo(user?.orgId, user?.plan),
        dashboardService.getTopPages()
      ]);

      setDashboardData({
        websiteInfo: websiteInfo.data,
        mlsConnections: mlsConnections.data,
        analytics: analytics.data,
        packageInfo: packageInfo.data,
        topPages: topPages.data,
        loading: false
      });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      setDashboardData(prev => ({ ...prev, loading: false }));
    }
  };

  const handleAnalyticsTimeRangeChange = async (newRange) => {
    setAnalyticsTimeRange(newRange);
    try {
      const analytics = await dashboardService.getAnalytics(newRange);
      setDashboardData(prev => ({ ...prev, analytics: analytics.data }));
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  // Initialize property SSE and polling when user is available
  React.useEffect(() => {
    if (!user || !user.orgId) {
      return;
    }

    // Initialize SSE connection for property events
    const eventSource = initializePropertySSE(API_BASE);
    
    // Start polling for property/page updates (fallback when editor not open)
    const stopPolling = startPropertyPagePolling(user.orgId, 30000); // Poll every 30 seconds
    
    // Listen for property:page:available events (from polling)
    const handlePageAvailable = (event) => {
      const { property, pageId, pageSlug } = event.detail;
      console.log('📄 Property page available:', { propertyId: property._id, pageId, pageSlug });
      
      // Show notification if available
      if (window.showNotification) {
        window.showNotification(`Property page created: ${pageSlug}`, 'success');
      }
    };
    
    window.addEventListener('property:page:available', handlePageAvailable);
    
    return () => {
      // Cleanup SSE
      if (eventSource) {
        closePropertySSE();
      }
      
      // Cleanup polling
      if (stopPolling) {
        stopPolling();
      }
      
      // Remove event listener
      window.removeEventListener('property:page:available', handlePageAvailable);
    };
  }, [user]);

  // Cleanup event listener on unmount
  React.useEffect(() => {
    const handleOpenDomainManager = () => {
      setDomainManagerOpen(true)
    }
    
    window.addEventListener('openDomainManager', handleOpenDomainManager)
    
    return () => {
      window.removeEventListener('openDomainManager', handleOpenDomainManager)
      // Cleanup GrapesJS event listeners
      if (window.cleanupGrapesJSListeners) {
        window.cleanupGrapesJSListeners();
      }
      // Cleanup property SSE
      closePropertySSE();
    }
  }, [])

  const onEditor = (editor) => {
    console.log('🎨 Editor loaded - initializing React plugins...')
    
    // Initialize React-based plugins
    initializeReactPlugins(editor)
    
    // Log plugin status
    const status = getPluginStatus()
    console.log('📦 Plugin Status:', status)
    
    // Store editor globally for debugging
    window.editor = editor

    // Drives the loading overlay below -- onReady is the documented
    // "safe to do anything" signal already relied on elsewhere in this
    // file (see the deal-prefill comment further down), not an assumption
    // specific to this feature.
    editor.onReady(() => setEditorReady(true))

    // Set up save status update function
    window.updateSaveStatus = (status) => {
      saveStatusRef.current.status = status;
      if (status === 'saved') {
        saveStatusRef.current.lastSaved = new Date();
        setLastSaved(new Date());
      }
      setSaveStatus(status);
    };
    
    // PRIORITY 3: Initialize change tracking
    setupChangeTracking(editor);
    console.log('✅ Change tracking initialized');
    
    // Setup GrapesJS Storage Events (following documentation best practices)
    setupStorageEvents(editor);
    
    // Setup native GrapesJS Page Events (following documentation best practices)
    setupPageEvents(editor);
    
    // PRIORITY 3: Setup offline queue auto-processing
    setupAutoProcess(async (data) => {
      // Process queued saves
      const token = localStorage.getItem('jwt') || localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/pages/grapesjs/store`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      return response.json();
    });
    console.log('✅ Offline queue initialized');
    
    // Initialize property page creation listener
    // Listens for new property events and automatically creates pages
    initializePropertyPageListener(editor, (property, pageResult) => {
      console.log('✅ Property page created successfully:', {
        propertyId: property._id,
        pageId: pageResult.pageId,
        pageSlug: pageResult.pageSlug
      });
      
      // Show notification
      if (window.showNotification) {
        window.showNotification(`Property page created: ${pageResult.pageSlug}`, 'success');
      }
    });
    console.log('✅ Property page creation listener initialized');
    
    // storageManager.autoload (see gjsOptions above) triggers our custom
    // load() handler automatically on init — it fetches saved pages itself
    // using the token from localStorage, so no manual editor.load() call
    // is needed here.

    // Native GrapesJS Pages API integration
    // The page events are now handled by setupPageEvents() function
    // which uses native GrapesJS page events (page:add, page:remove, page:select, page:update)
    
    // Store Pages API reference globally for external components
    window.grapesJSPages = editor.Pages;
    
    // Helper functions for external components to interact with pages
    window.createGrapesJSPage = (pageData) => {
      console.log('📄 Creating page via GrapesJS API:', pageData);
      const newPage = editor.Pages.add({
        id: pageData._id || pageData.id,
        name: pageData.name || 'New Page',
        component: pageData.components || pageData.component || '<div>New page content</div>',
        styles: pageData.styles || ''
      });
      
      // Select the new page
      if (newPage) {
        editor.Pages.select(newPage);
      }
      
      return newPage;
    };
    
    window.deleteGrapesJSPage = (pageId) => {
      console.log('🗑️ Deleting page via GrapesJS API:', pageId);
      const page = editor.Pages.get(pageId);
      if (page) {
        editor.Pages.remove(page);
        return true;
      }
      return false;
    };
    
    window.selectGrapesJSPage = (pageId) => {
      console.log('📄 Selecting page via GrapesJS API:', pageId);
      const page = editor.Pages.get(pageId);
      if (page) {
        editor.Pages.select(page);
        return true;
      }
      return false;
    };

    // Store cleanup function for Pages API
    window.cleanupGrapesJSListeners = () => {
      // Native GrapesJS events are automatically cleaned up by the editor
      // Just clean up our global references
      delete window.grapesJSPages;
      delete window.createGrapesJSPage;
      delete window.deleteGrapesJSPage;
      delete window.selectGrapesJSPage;
      console.log('🧹 Cleaned up GrapesJS Pages API references');
    };

    // Debug canvas and drag-drop functionality
    console.log('🎯 Canvas element:', editor.Canvas.getElement())
    console.log('🎯 Canvas wrapper:', editor.Canvas.getWrapperEl ? editor.Canvas.getWrapperEl() : 'Method not available')
    console.log('🎯 Block manager:', editor.BlockManager)
    console.log('🎯 Drag-drop enabled:', editor.Canvas.getConfig().dropzone)
    
    // Check if blocks are registered
    const blocks = editor.BlockManager.getAll()
    console.log('🎯 Registered blocks:', blocks.length)
    blocks.forEach(block => {
      console.log('🎯 Block:', block.get('label'), 'ID:', block.get('id'))
    })
    
    // Add drag-drop event listeners for debugging
    // NOTE: block:drag:stop fires with block === null on this GrapesJS
    // version for at least one drop path — block.get('label') here used to
    // throw synchronously inside GrapesJS's own internal __endDrag/trigger
    // call chain (confirmed via browser stack trace), which aborted
    // GrapesJS's end-of-drag handling before it could insert the block
    // into the canvas. Every block drag silently failed to drop because of
    // this debug log. Guard with optional chaining instead of assuming a
    // non-null block on either event.
    editor.on('block:drag:start', (block) => {
      console.log('🎯 Block drag start:', block?.get('label'))
    })

    editor.on('block:drag:stop', (block) => {
      console.log('🎯 Block drag stop:', block?.get('label'))
    })
    
    
    editor.on('canvas:drop', (data) => {
      console.log('🎯 Canvas drop event:', data)
    })
    
    // Test block dragging
    setTimeout(() => {
      console.log('🎯 Testing block drag functionality...')
      const testBlock = editor.BlockManager.get('basic-section')
      if (testBlock) {
        console.log('🎯 Test block found:', testBlock.get('label'))
        console.log('🎯 Block element:', testBlock.get('el'))
        console.log('🎯 Block draggable:', testBlock.get('draggable'))
      } else {
        console.log('🎯 No test block found')
      }
      
      // Check canvas drop zone
      const canvasEl = editor.Canvas.getElement()
      console.log('🎯 Canvas drop zone enabled:', canvasEl.hasAttribute('data-highlightable'))
      console.log('🎯 Canvas pointer events:', getComputedStyle(canvasEl).pointerEvents)
      
      // Check block manager
      const blockManagerEl = document.querySelector('.gjs-blocks-c')
      if (blockManagerEl) {
        console.log('🎯 Block manager found:', blockManagerEl)
        console.log('🎯 Block manager pointer events:', getComputedStyle(blockManagerEl).pointerEvents)
      } else {
        console.log('🎯 Block manager not found')
      }
    }, 2000)

    // Add template loading function
    window.loadTemplate = async (templateId) => {
      try {
        const response = await fetch(`${API_BASE}/api/pages/templates/${templateId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        })
        
        if (response.ok) {
          const template = await response.json()
          
          // Clear current content
          editor.DomComponents.clear()
          editor.CssComposer.clear()
          
          // Load template content
          editor.setComponents(template.html)
          editor.setStyle(template.css)
          
          console.log(`✅ Template "${template.name}" loaded successfully`)
          return template
        } else {
          console.error('Failed to load template:', response.statusText)
        }
      } catch (error) {
        console.error('Error loading template:', error)
      }
    }
    
    // Note: Page switching and saving now handled by GrapesJS Storage Manager
    // Auto-saves to MongoDB via /api/pages/grapesjs/store endpoint
    
    // Set up global functions for AI Assistant Panel
    window.getEditorComponents = () => {
      return editor.getComponents().toHTML();
    };
    
    window.getEditorStyles = () => {
      return editor.getCss();
    };
    
    window.setEditorContent = (html, css) => {
      editor.setComponents(html);
      if (css) editor.setStyle(css);
    };
    
    window.showNotification = (message, type = 'info') => {
      // Simple notification implementation
      const notification = document.createElement('div');
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        padding: 12px 20px;
        border-radius: 4px;
        color: white;
        font-weight: 500;
        z-index: 10000;
        background: ${type === 'success' ? '#4caf50' : type === 'error' ? '#f44336' : '#2196f3'};
      `;
      notification.textContent = message;
      document.body.appendChild(notification);
      setTimeout(() => notification.remove(), 3000);
    };

    // Deal Board → Template Builder: one-shot prefill of a listing page from
    // a CRM deal, triggered by the "Create Website" SSO hop (activeCrmDeal
    // set server-side on GET /api/auth/auto-login). Runs once per link —
    // marks prefillPending false immediately so a page refresh won't re-run
    // it and stomp the agent's subsequent edits. Must run after
    // window.showNotification is defined above, or success/error toasts
    // silently no-op on a fresh page load.
    // NOTE: plain console.log/console.error calls are silently stripped from
    // this app's production build (confirmed by inspecting the deployed
    // bundle — window.showNotification text literals survive, raw
    // console.error string literals from the same commit do not). This
    // codebase's own debug.force()/debug.error() indirect through
    // Function.prototype.bind specifically to survive that stripping — use
    // those, not console.* directly, for anything that needs to be visible
    // in production.
    debug.force('[deal-prefill] onEditor fired. user.activeCrmDeal =', JSON.stringify(user?.activeCrmDeal));
    // storageManager.autoload triggers our custom load() asynchronously —
    // it may still be in flight when onEditor fires, and its result can
    // replace/merge into editor.Pages after this point. Running the prefill
    // logic before that resolves risks it either seeing a stale/incomplete
    // page list, or having its own freshly-created page discarded once the
    // real load result lands. Wait for the load to actually finish (with a
    // timeout fallback in case autoload never fires — e.g. no auth token).
    const runDealPrefill = () => {
    const linkedDeal = user?.activeCrmDeal;
    if (linkedDeal?.dealId) {
      const pageId = dealPageId(linkedDeal.dealId);
      const existingPage = editor.Pages.get(pageId);
      const existingPageHasContent = existingPage
        && (existingPage.getMainComponent?.()?.components?.().length ?? 0) > 0;
      // Gate strictly on crmDealRedirect -- derived fresh from THIS
      // redirect's own ?source=deal (backend only sets it when its own
      // deal_id was present, routes/auth.js) -- rather than
      // linkedDeal.prefillPending or the broader arrivedViaCrmRedirect
      // (true for every SSO hop, deal or not). prefillPending is
      // server-persisted and resurfaces on ordinary logins long after the
      // redirect, which was causing this to fire (and create/overwrite deal
      // pages) on every plain editor open -- including ones that never
      // carried a deal at all. Once we know this load *is* a deal redirect,
      // "no real page exists yet for this deal" is still used as the
      // trigger (not prefillPending) so it stays self-healing against a
      // stale/failed earlier attempt within that same landing.
      const shouldPrefill = crmDealRedirect && !existingPageHasContent;
      debug.force('[deal-prefill] pageId =', pageId, 'existingPage =', !!existingPage, 'hasContent =', existingPageHasContent, 'crmDealRedirect =', crmDealRedirect, 'shouldPrefill =', shouldPrefill);

      if (shouldPrefill) {
        // Pages.add()/resetFromString()/setStyle()/Pages.select() below fire
        // the exact same component:add / style:update / page:add / page:select
        // events a real user edit would -- changeTracking.js's page:add and
        // page:select listeners, plus GrapesJS's own stepsBeforeSave counter,
        // were independently queuing up to 3 overlapping autosaves within
        // 1-2s of the editor mounting, before the user had done anything.
        // Suppress both save paths for the duration of this block, then fire
        // exactly one deliberate store() once prefill has actually finished.
        window._gjsPrefillInProgress = true;
        const wasAutosaveEnabled = editor.StorageManager.isAutosave();
        editor.StorageManager.setAutosave(false);
        let didCreateContent = false;
        try {
          const deal = linkedDeal;

          if (existingPage && existingPageHasContent) {
            // Agent revisited "Create Website" for a deal that already has a
            // real page — select it instead of recreating/overwriting their edits.
            debug.force('[deal-prefill] selecting existing page with content');
            editor.Pages.select(existingPage);
          } else {
            // Either a brand new page, or a leftover empty page from an
            // earlier failed attempt — safe to (re)generate content into it
            // either way, nothing real to lose.
            const pageName = deal.address ? `Listing - ${deal.address}` : `Listing ${deal.dealId}`;
            const newPage = existingPage || editor.Pages.add({ id: pageId, name: pageName });
            if (existingPage) existingPage.setName?.(pageName);
            debug.force('[deal-prefill] newPage =', newPage, 'id =', newPage?.getId?.());

            if (!newPage) {
              throw new Error('editor.Pages.add() returned nothing');
            }

            // Populate the page's component tree directly on the model,
            // BEFORE selecting it — root.components().resetFromString() is a
            // synchronous, model-only operation (no canvas view required),
            // unlike editor.setComponents(), which acts on whatever page is
            // currently *selected* and needs a mounted frame view to attach
            // into. The previous version called Pages.select() first and
            // deferred setComponents() to the canvas's frame-load event to
            // work around that — but selecting a page whose frame isn't
            // populated yet, then mutating it out from under GrapesJS's own
            // in-flight mount, is exactly the kind of race that was crashing
            // canvas mount elsewhere in this app (see globalNavbar.ts). Doing
            // it in this order means the frame is already fully-formed by
            // the time anything (GrapesJS or us) ever tries to render it.
            const { html: dealBodyHtml, css } = buildDealListingPage(deal);
            debug.force('[deal-prefill] dealBodyHtml length =', dealBodyHtml?.length);
            // There's no live "template" system in this app — every page
            // just bakes in its own copy of header/nav/footer HTML. To make
            // this page actually look like it belongs to the site (not a
            // disconnected generic page), clone the real header/footer off
            // the org's home page and wrap the deal content in it. Falls
            // back to a standalone page if the home page has no top-level
            // <main> to split on.
            const chrome = extractSiteChrome(editor);
            debug.force('[deal-prefill] chrome =', chrome ? { headerLen: chrome.headerHtml.length, footerLen: chrome.footerHtml.length } : null);
            const finalHtml = chrome
              ? `${chrome.headerHtml}\n<main>${dealBodyHtml}</main>\n${chrome.footerHtml}`
              : dealBodyHtml;

            newPage.getMainComponent().components().resetFromString(finalHtml);
            // editor.setStyle() replaces the *entire* project stylesheet,
            // not just this page's — append rather than overwrite so every
            // other page's CSS survives. getCss() (not getStyle(), which
            // returns a CssRules collection, not a string) is the text form.
            // This is a pure CSS-data operation (not tied to any canvas
            // view), so it's safe regardless of mount timing.
            editor.setStyle(`${editor.getCss() || ''}\n${css}`);
            debug.force('[deal-prefill] content set. main component child count =',
              newPage.getMainComponent()?.components?.().length);

            editor.Pages.select(newPage);
            debug.force('[deal-prefill] selected. currently selected page id =', editor.Pages.getSelected?.()?.getId?.());
            didCreateContent = true;

            window.showNotification(`Created listing page for ${deal.address || 'deal'}`, 'success');

            // Best-effort — only meaningful if prefillPending was actually
            // true; harmless no-op otherwise. Not the gate for whether
            // prefill runs (see shouldPrefill above), just cleanup.
            if (linkedDeal.prefillPending) {
              apiService.clearDealPrefillPending().catch((err) => {
                debug.error('[deal-prefill] Failed to clear deal prefill pending flag:', err.message);
              });
            }
          }
        } catch (err) {
          debug.error('[deal-prefill] Failed to create/select deal listing page:', err);
          window.showNotification(`Couldn't create listing page: ${err.message}`, 'error');
        } finally {
          window._gjsPrefillInProgress = false;
          editor.StorageManager.setAutosave(wasAutosaveEnabled);
          if (didCreateContent) {
            // The single intentional save for everything prefill just did --
            // replaces the 2-3 overlapping autosaves this used to trigger.
            editor.store().catch((err) => {
              debug.error('[deal-prefill] Failed to save newly created deal page:', err.message);
            });
          }
        }
      } else {
        debug.force('[deal-prefill] SKIPPED — existing page already has real content, nothing to do');
      }
    } else {
      debug.force('[deal-prefill] SKIPPED — no linked deal on this user');
    }
    };

    // IMPORTANT: everything above only *defines* runDealPrefill — actually
    // calling it must wait for editor.onReady(), not 'canvas:frame:load' (an
    // earlier attempt used that gate and still crashed). Per GrapesJS's own
    // event docs, 'canvas:frame:load' fires "right after iframe's onload" —
    // BEFORE the canvas's injected scripts (Tailwind CDN, UIkit, sequential,
    // can take 1s+) finish loading, and before the frame's body is actually
    // rendered ('canvas:frame:load:body' is the event for that). onReady is
    // the documented "safe to do anything" signal — it's the same one
    // @grapesjs/react itself waits on before ever showing the real canvas
    // instead of its own loading placeholder. Running Pages.add()/select()
    // before that point was racing GrapesJS's own initial mount.
    editor.onReady(() => {
      let dealPrefillRan = false;
      const onStorageLoadEnd = () => {
        if (dealPrefillRan) return;
        dealPrefillRan = true;
        editor.off('storage:end:load', onStorageLoadEnd);
        debug.force('[deal-prefill] storage:end:load fired, running prefill check');
        runDealPrefill();
      };
      editor.on('storage:end:load', onStorageLoadEnd);
      setTimeout(() => {
        if (dealPrefillRan) return;
        dealPrefillRan = true;
        editor.off('storage:end:load', onStorageLoadEnd);
        debug.force('[deal-prefill] storage:end:load never fired, running prefill check via fallback timeout');
        runDealPrefill();
      }, 1200);
    });

    // Add manual save function using GrapesJS Storage Manager
    window.saveCurrentPage = async () => {
      try {
        if (window.editor) {
          await window.editor.store();
          const currentPage = window.editor.Pages.getSelected();
          if (currentPage) {
            window.showNotification(`Saved page: ${currentPage.getName()}`, 'success');
          }
        }
      } catch (error) {
        console.error('Error saving page:', error);
        window.showNotification('Error saving page', 'error');
      }
    };
    
    // Add function to save all pages to database (triggers GrapesJS storage)
    window.saveAllPagesToDatabase = async () => {
      try {
        if (window.editor) {
          await window.editor.store();
          window.showNotification('All pages saved to database!', 'success');
        }
      } catch (error) {
        console.error('❌ Error saving pages:', error);
        window.showNotification('Error saving pages: ' + error.message, 'error');
      }
    };
    
    // Add function to view save metrics
    window.getSaveMetrics = () => {
      if (!window.saveMetrics) {
        console.log('📊 No save metrics available yet');
        return null;
      }
      
      const metrics = window.saveMetrics;
      const successRate = ((metrics.successfulSaves / metrics.totalSaves) * 100).toFixed(2);
      
      console.log('📊 Save Performance Metrics:');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log(`Total Saves:       ${metrics.totalSaves}`);
      console.log(`Successful:        ${metrics.successfulSaves} (${successRate}%)`);
      console.log(`Failed:            ${metrics.failedSaves}`);
      console.log(`Total Retries:     ${metrics.totalRetries}`);
      console.log(`Average Save Time: ${metrics.averageSaveTime.toFixed(2)}ms`);
      console.log(`Last Save Time:    ${metrics.saveTimes[metrics.saveTimes.length - 1]?.toFixed(2)}ms`);
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      
      return metrics;
    };
    
    // Add function to reset save metrics
    window.resetSaveMetrics = () => {
      window.saveMetrics = {
        totalSaves: 0,
        successfulSaves: 0,
        failedSaves: 0,
        totalRetries: 0,
        averageSaveTime: 0,
        saveTimes: []
      };
      console.log('📊 Save metrics reset');
    };
    
    // PRIORITY 3: Add compression metrics viewer
    window.getCompressionMetrics = () => {
      if (!window.compressionMetrics) {
        console.log('🗜️ No compression metrics available yet');
        return null;
      }
      
      const metrics = window.compressionMetrics;
      console.log('🗜️ Compression Metrics:');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log(`Total Compressed:  ${metrics.totalCompressed}`);
      console.log(`Total Saved:       ${formatBytes(metrics.totalSaved)}`);
      console.log(`Average Ratio:     ${metrics.averageRatio.toFixed(2)}x`);
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      
      return metrics;
    };
    
    // PRIORITY 3: Add offline queue viewer
    window.getQueueStats = async () => {
      const stats = await getQueueStats();
      console.log('📥 Offline Queue Stats:');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log(`Total:             ${stats.total}`);
      console.log(`Pending:           ${stats.pending}`);
      console.log(`Completed:         ${stats.completed}`);
      console.log(`Failed:            ${stats.failed}`);
      if (stats.oldestPending) {
        const age = Date.now() - stats.oldestPending;
        console.log(`Oldest Pending:    ${Math.round(age / 1000)}s ago`);
      }
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      
      return stats;
    };
    
    // PRIORITY 3: Add change tracking viewer
    window.changeTracker = changeTracker;
    window.getChangeStats = () => {
      const stats = changeTracker.getStats();
      console.log('📝 Change Tracking Stats:');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log(`Dirty Pages:       ${stats.dirtyPages}`);
      console.log(`Total Snapshots:   ${stats.totalSnapshots}`);
      console.log(`Recent Changes:    ${stats.recentChanges.length}`);
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      
      return stats;
    };
    
    // PRIORITY 3: Manual queue processing
    window.processOfflineQueue = async () => {
      console.log('🔄 Manually processing offline queue...');
      const result = await processQueue(async (data) => {
        const token = localStorage.getItem('jwt') || localStorage.getItem('token');
        const response = await fetch(`${API_BASE}/api/pages/grapesjs/store`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(data)
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        return response.json();
      });
      
      console.log(`✅ Processed ${result.processed} items: ${result.successful} successful, ${result.failed} failed`);
      return result;
    };
  }

  const handleOpenAIGenerator = () => {
    setAIGeneratorOpen(true);
  };

  const handleCloseAIGenerator = () => {
    setAIGeneratorOpen(false);
  };

  const handleGenerateWebsite = async (generatedContent) => {
    const editor = window.editor;
    if (!editor || !generatedContent) {
      console.warn('❌ Editor or generated content not available');
      return;
    }

    try {
      console.log('🚀 Starting AI website injection...', generatedContent);
      
      // Handle multi-page content properly
      const isMultiPage = generatedContent.pages && Object.keys(generatedContent.pages).length > 1;
      
      if (isMultiPage) {
        console.log(`📄 Creating ${Object.keys(generatedContent.pages).length} pages...`);
        
        // Clear existing content
        editor.Commands.run('core:canvas-clear');
        editor.DomComponents.clear();
        editor.CssComposer.clear();
        
        // Add CSS styles
        if (generatedContent.css) {
          editor.setStyle(generatedContent.css);
        }
        
        // Get the page manager
        const pageManager = editor.Pages;
        
        // Clear existing pages except the main one
        const existingPages = pageManager.getAll();
        existingPages.slice(1).forEach(page => pageManager.remove(page));
        
        // Process each page
        let pageIndex = 0;
        for (const [pageName, pageHtml] of Object.entries(generatedContent.pages)) {
          if (pageIndex === 0) {
            // Use the existing main page for the first page
            const mainPage = pageManager.getMain();
            mainPage.setName(pageName.charAt(0).toUpperCase() + pageName.slice(1));
            
            // Set content for main page
            editor.setComponents(pageHtml);
            console.log(`✅ Set main page: ${pageName}`);
          } else {
            // Create new pages for additional content
            const newPage = pageManager.add({
              name: pageName.charAt(0).toUpperCase() + pageName.slice(1),
              component: pageHtml
            });
            
            console.log(`✅ Created page: ${pageName}`);
          }
          pageIndex++;
        }
        
        // Switch back to the first page
        const firstPage = pageManager.getAll()[0];
        if (firstPage) {
          pageManager.select(firstPage);
        }
        
        console.log(`✅ Successfully created ${Object.keys(generatedContent.pages).length} pages`);
        
        // Debug: List all pages
        const allPages = pageManager.getAll();
        console.log('📄 All pages in Page Manager:', allPages.map(page => ({
          id: page.getId(),
          name: page.getName(),
          main: page.isMain()
        })));
        
        // Sync GrapesJS pages with backend database
        try {
          console.log('🔄 Syncing GrapesJS pages with backend database...');
          
          // Get the current user's token
          const token = localStorage.getItem('jwt') || localStorage.getItem('token');
          console.log('🔑 Token found:', token ? 'Yes' : 'No');
          if (token) {
            console.log('🔑 Token preview:', token.substring(0, 20) + '...');
            
            // Create backend pages for each GrapesJS page
            for (const [pageName, pageHtml] of Object.entries(generatedContent.pages)) {
              const pageData = {
                name: pageName.charAt(0).toUpperCase() + pageName.slice(1),
                slug: pageName.toLowerCase().replace(/\s+/g, '-'),
                title: `${pageName.charAt(0).toUpperCase() + pageName.slice(1)} - AI Generated`,
                metaDescription: `AI-generated ${pageName} page`,
                status: 'draft',
                template: 'custom',
                isHomePage: pageName === 'home',
                components: { html: pageHtml },
                styles: { css: generatedContent.css || '' }
              };
              
            console.log(`📄 Creating page: ${pageName}`, pageData);
            
            // Create page in backend
            const response = await fetch(`${API_BASE}/api/pages`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify(pageData)
            });              const responseData = await response.json();
              
              if (response.ok) {
                console.log(`✅ Created backend page: ${pageName}`, responseData);
              } else {
                console.warn(`⚠️ Failed to create backend page: ${pageName}`, responseData);
              }
            }
            
            // Trigger page context refresh
            window.dispatchEvent(new CustomEvent('refreshPages'));
          } else {
            console.log('⚠️ No authentication token found, skipping backend sync');
          }
        } catch (error) {
          console.error('❌ Error syncing pages with backend:', error);
        }
        
        // Show success notification
        if (window.showNotification) {
          window.showNotification(`Successfully generated ${Object.keys(generatedContent.pages).length} pages! Check the Page Manager to see all pages.`, 'success');
        }
      } else {
        // Handle single page content (legacy format)
        console.log('📄 Creating single page...');
        
        editor.Commands.run('core:canvas-clear');
        editor.DomComponents.clear();
        editor.CssComposer.clear();
        
        const htmlContent = generatedContent.html || 
          (generatedContent.pages ? generatedContent.pages.home : '');
        editor.setComponents(htmlContent);
        editor.setStyle(generatedContent.css || '');
        
        console.log('✅ Single page created successfully');
      }
      
      // Refresh the editor
      editor.refresh();
      
    } catch (error) {
      console.error('❌ Error creating pages:', error);
      
      // Fallback to simple injection
      try {
        editor.Commands.run('core:canvas-clear');
        editor.DomComponents.clear();
        editor.CssComposer.clear();
        
        const htmlContent = generatedContent.html || 
          (generatedContent.pages ? generatedContent.pages.home : '');
        editor.setComponents(htmlContent);
        editor.setStyle(generatedContent.css || '');
        
        console.log('⚠️ Fallback injection completed');
      } catch (fallbackError) {
        console.error('❌ All injection methods failed:', fallbackError);
      }
    }
  };



  // Page manager handlers removed - now using native GrapesJS page manager
  // const handleOpenPageManager = () => {
  //   setPageManagerOpen(true);
  // };

  // const handleClosePageManager = () => {
  //   setPageManagerOpen(false);
  // };

  const handleOpenDomainManager = () => {
    setDomainManagerOpen(true);
  };

  const handleCloseDomainManager = () => {
    setDomainManagerOpen(false);
  };

  const handleOpenAnalyticsDashboard = () => {
    setAnalyticsDashboardOpen(true);
  };

  const handleCloseAnalyticsDashboard = () => {
    setAnalyticsDashboardOpen(false);
  };

  const handleOpenTrackingSetup = () => {
    setTrackingSetupOpen(true);
  };

  const handleCloseTrackingSetup = () => {
    setTrackingSetupOpen(false);
  };

  const handleOpenGoogleAnalyticsSetup = () => {
    setGoogleAnalyticsSetupOpen(true);
  };

  const handleCloseGoogleAnalyticsSetup = () => {
    setGoogleAnalyticsSetupOpen(false);
  };

  const handleOpenFacebookPixelSetup = () => {
    setFacebookPixelSetupOpen(true);
  };

  const handleCloseFacebookPixelSetup = () => {
    setFacebookPixelSetupOpen(false);
  };

  const handleOpenMapboxSettings = () => {
    setMapboxSettingsOpen(true);
  };

  const handleCloseMapboxSettings = () => {
    setMapboxSettingsOpen(false);
  };

  console.log('🎨 Rendering GoDreamX Web Builder JSX...');
  
  return (
    <ThemeProvider theme={theme}>
      <DomainProvider>
          {/* Keep MainNav mounted when editor is open so window.handlePublish stays registered */}
          <div style={{ display: editorOpen ? 'none' : 'block' }}>
            <MainNav
              onOpenEditor={() => setEditorOpen(true)}
              onCloseEditor={() => setEditorOpen(false)}
              onOpenSettings={() => setSettingsOpen(true)}
            />
          </div>

          {editorOpen && !editorReady && (
            <div style={{
              position: 'fixed', inset: 0, zIndex: 9999,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 16, background: '#0d0d0f',
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8,
                background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                display: 'grid', placeItems: 'center',
              }}>
                <div style={{
                  width: 16, height: 16, borderRadius: '50%',
                  border: '2px solid rgba(255,255,255,0.35)', borderTopColor: '#fff',
                  animation: 'dx-editor-spin 0.8s linear infinite',
                }} />
              </div>
              <div style={{ color: '#6b6b76', fontSize: 13, fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif' }}>
                Loading editor&hellip;
              </div>
              <style>{'@keyframes dx-editor-spin { to { transform: rotate(360deg); } }'}</style>
            </div>
          )}

          {editorOpen ? (
          <GjsEditor
            className="gjs-custom-editor"
            grapesjs={grapesjs}
            options={gjsOptions}
            plugins={[customStoragePlugin, ...getAllPlugins()]}
            onEditor={onEditor}
          >
            <div className="dreamx-shell">
              {/* Top navigation bar */}
              <WithEditor>
                <Topbar
                  onGoToOverview={() => setEditorOpen(false)}
                  onOpenSettings={() => setSettingsOpen(true)}
                />
              </WithEditor>

              {/* Workspace: canvas + right panel */}
              <div className="dreamx-workspace">
                {/* Canvas area with floating toolbar */}
                <div className="dreamx-canvas-area">
                  <WithEditor>
                    <CanvasToolbar saveStatus={saveStatus} lastSaved={lastSaved} />
                  </WithEditor>
                  <Canvas style={{ flex: 1, minHeight: 0 }} />
                </div>

                {/* Right panel */}
                <RightSidebar />
              </div>
            </div>
            <AssetsProvider>
              {({ assets, select, close, Container }) => (
                <Container>
                  {/* Asset manager content */}
                </Container>
              )}
            </AssetsProvider>
          </GjsEditor>
          ) : (
            /* ─── Overview Dashboard ─────────────────────────────────────── */
            <DashboardOverview
              dashboardData={dashboardData}
              user={user}
              currentSubdomain={currentSubdomain}
              analyticsTimeRange={analyticsTimeRange}
              onTimeRangeChange={handleAnalyticsTimeRangeChange}
              onOpenEditor={() => setEditorOpen(true)}
              onOpenAIGenerator={() => setAIGeneratorOpen(true)}
              onOpenNavSettings={() => setNavSettingsOpen(true)}
              onOpenSettings={() => setSettingsOpen(true)}
            />
          )}

          {/* Navigation Settings Modal */}
          <NavigationSettingsModal
            open={navSettingsOpen}
            onClose={() => setNavSettingsOpen(false)}
          />

          {/* AI Website Generator Modal */}
          {aiGeneratorOpen && (
            <React.Suspense fallback={SuspenseLoader()}>
              <AIWebsiteGenerator
                open={aiGeneratorOpen}
                onClose={handleCloseAIGenerator}
                onGenerateWebsite={handleGenerateWebsite}
              />
            </React.Suspense>
          )}

          {/* Domain Manager Modal */}
          {domainManagerOpen && (
            <ErrorBoundary>
              <React.Suspense fallback={SuspenseLoader()}>
                <DomainManager
                  open={domainManagerOpen}
                  onClose={handleCloseDomainManager}
                />
              </React.Suspense>
            </ErrorBoundary>
          )}

          {/* Analytics Dashboard Modal */}
          <Dialog
            open={analyticsDashboardOpen}
            onClose={handleCloseAnalyticsDashboard}
            maxWidth="xl"
            fullWidth
            sx={{ 
              '& .MuiDialog-paper': { 
                maxHeight: '90vh',
                height: '90vh'
              } 
            }}
          >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              Analytics Dashboard
              <IconButton onClick={handleCloseAnalyticsDashboard}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <React.Suspense fallback={SuspenseLoader()}>
                <AnalyticsDashboard />
              </React.Suspense>
            </DialogContent>
          </Dialog>

          {/* Tracking Setup Modal */}
          <Dialog
            open={trackingSetupOpen}
            onClose={handleCloseTrackingSetup}
            maxWidth="lg"
            fullWidth
            sx={{ 
              '& .MuiDialog-paper': { 
                maxHeight: '90vh'
              } 
            }}
          >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              Lead Tracking Setup
              <IconButton onClick={handleCloseTrackingSetup}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <React.Suspense fallback={SuspenseLoader()}>
                <TrackingSetup />
              </React.Suspense>
            </DialogContent>
          </Dialog>

          {/* Google Analytics Setup Modal */}
          <Dialog
            open={googleAnalyticsSetupOpen}
            onClose={handleCloseGoogleAnalyticsSetup}
            maxWidth="lg"
            fullWidth
            sx={{ 
              '& .MuiDialog-paper': { 
                maxHeight: '90vh'
              } 
            }}
          >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              Google Analytics Setup
              <IconButton onClick={handleCloseGoogleAnalyticsSetup}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <React.Suspense fallback={SuspenseLoader()}>
                <GoogleAnalyticsSetup />
              </React.Suspense>
            </DialogContent>
          </Dialog>

          {/* Facebook Pixel Setup Modal */}
          <Dialog
            open={facebookPixelSetupOpen}
            onClose={handleCloseFacebookPixelSetup}
            maxWidth="lg"
            fullWidth
            sx={{ 
              '& .MuiDialog-paper': { 
                maxHeight: '90vh'
              } 
            }}
          >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              Facebook Pixel Setup
              <IconButton onClick={handleCloseFacebookPixelSetup}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <React.Suspense fallback={SuspenseLoader()}>
                <FacebookPixelSetup />
              </React.Suspense>
            </DialogContent>
          </Dialog>

          {/* Mapbox Settings Modal */}
          {mapboxSettingsOpen && (
            <React.Suspense fallback={SuspenseLoader()}>
              <MapboxSettings
                open={mapboxSettingsOpen}
                onClose={handleCloseMapboxSettings}
              />
            </React.Suspense>
          )}

          {/* Settings Modal */}
          {settingsOpen && (
            <React.Suspense fallback={SuspenseLoader()}>
              <SettingsModal
                open={settingsOpen}
                onClose={() => setSettingsOpen(false)}
                user={user}
                handlers={{
                  onOpenDomainManager: () => setDomainManagerOpen(true),
                  onOpenAnalyticsDashboard: () => setAnalyticsDashboardOpen(true),
                  onOpenTrackingSetup: () => setTrackingSetupOpen(true),
                  onOpenGoogleAnalyticsSetup: () => setGoogleAnalyticsSetupOpen(true),
                  onOpenFacebookPixelSetup: () => setFacebookPixelSetupOpen(true),
                  onOpenMapboxSettings: () => setMapboxSettingsOpen(true)
                }}
              />
            </React.Suspense>
          )}
        </DomainProvider>
    </ThemeProvider>
  )
} 

